import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { LayoutDashboard, Users, Store, GraduationCap, Award, CreditCard, BarChart3, Bell, UserCheck, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "users", label: "All Users", icon: Users },
  { key: "shopkeepers", label: "Shopkeepers", icon: Store },
  { key: "freelancers", label: "Freelancers", icon: UserCheck },
  { key: "training", label: "Training", icon: GraduationCap },
  { key: "certs", label: "Certificates", icon: Award },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function AdminDashboard() {
  const [hash, setHash] = useState(window.location.hash.replace("#", "") || "overview");
  useEffect(() => {
    const on = () => setHash(window.location.hash.replace("#", "") || "overview");
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  const titles = { overview: "Admin Overview", users: "All Users", shopkeepers: "Shopkeepers", freelancers: "Freelancers", training: "Manage Training", certs: "Certificates", payments: "All Payments", analytics: "Analytics" };
  return (
    <DashboardLayout nav={NAV} title={titles[hash]}>
      {hash === "overview" && <Overview />}
      {hash === "users" && <UsersList role="" />}
      {hash === "shopkeepers" && <UsersList role="shopkeeper" />}
      {hash === "freelancers" && <UsersList role="freelancer" showApprove />}
      {hash === "training" && <TrainingAdmin />}
      {hash === "certs" && <CertsAdmin />}
      {hash === "payments" && <PaymentsAdmin />}
      {hash === "analytics" && <Analytics />}
    </DashboardLayout>
  );
}

function Overview() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)); }, []);
  const kpis = [
    ["Shopkeepers", s.shopkeepers, "blue"], ["Freelancers", s.freelancers, "blue"],
    ["Pending approval", s.pending_freelancers, "orange"], ["Active assignments", s.assignments, "blue"],
    ["Open tasks", s.tasks_open, "orange"], ["Tasks done", s.tasks_done, "blue"],
    ["Courses", s.courses, "blue"], ["Certificates", s.certificates, "orange"],
  ];
  return (
    <div className="space-y-6">
      <div className="card-soft p-8" style={{ background: "linear-gradient(135deg,#0F172A 0%,#155EEF 100%)" }}>
        <div className="text-white/70 text-sm">SELLEXR Admin</div>
        <h2 className="mt-1 font-heading font-bold text-3xl text-white">Platform control center</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(([l, v, t]) => (
          <div key={l} className="card-soft p-6">
            <div className="text-xs uppercase tracking-wider text-slate-500">{l}</div>
            <div className="mt-2 font-heading font-bold text-3xl" style={{ color: t === "orange" ? "#FF6A00" : "#155EEF" }}>{v ?? 0}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersList({ role, showApprove }) {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users" + (role ? `?role=${role}` : "")).then(r => setUsers(r.data));
  useEffect(() => { load(); }, [role]);
  const approve = async (id) => {
    try {
      await api.post(`/admin/users/${id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed. Please try again.");
    }
  };
  const reject = async (id) => {
    try {
      await api.post(`/admin/users/${id}/reject`);
      toast.success("Suspended");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suspension failed. Please try again.");
    }
  };
  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed. Please try again.");
    }
  };
  return (
    <div className="card-soft overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50"><tr>{["User", "Email", "Role", "City", "Status", "Actions"].map(h => <th key={h} className="text-left p-4 text-xs uppercase tracking-wider text-slate-500 font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id} className="border-b border-slate-100" data-testid={`user-${u.id}`}>
              <td className="p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: u.avatar_color || "#155EEF" }}>{u.name?.[0]}</div>
                <span className="font-semibold">{u.name}</span>
              </td>
              <td className="p-4 text-slate-500">{u.email}</td>
              <td className="p-4"><Badge variant="secondary">{u.role}</Badge></td>
              <td className="p-4 text-slate-500">{u.city || "—"}</td>
              <td className="p-4">
                {u.verified ? (u.approved ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge> : <Badge className="bg-[#FF6A00]/10 text-[#FF6A00] hover:bg-[#FF6A00]/10">Pending</Badge>) : <Badge variant="outline">Unverified</Badge>}
              </td>
              <td className="p-4">
                <div className="flex gap-2">
                  {showApprove && !u.approved && <Button size="sm" onClick={() => approve(u.id)} className="btn-orange hover:bg-[#FF6A00] h-8 text-xs" data-testid={`approve-${u.id}`}>Approve</Button>}
                  {showApprove && u.approved && u.role === "freelancer" && <Button size="sm" variant="outline" onClick={() => reject(u.id)} className="h-8 text-xs" data-testid={`reject-${u.id}`}>Suspend</Button>}
                  <Button size="sm" variant="ghost" onClick={() => del(u.id)} data-testid={`del-user-${u.id}`}><Trash2 size={14} className="text-red-500" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TrainingAdmin() {
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", youtube_id: "", duration_min: 30, quiz_json: "" });
  const load = () => api.get("/courses").then(r => setCourses(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    let quiz;
    try { quiz = JSON.parse(f.quiz_json); } catch { toast.error("Invalid quiz JSON"); return; }
    await api.post("/courses", { ...f, duration_min: +f.duration_min, quiz });
    toast.success("Course added"); setOpen(false); setF({ title: "", description: "", youtube_id: "", duration_min: 30, quiz_json: "" }); load();
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/courses/${id}`); load(); };
  return (
    <div className="space-y-4">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="btn-orange hover:bg-[#FF6A00]" data-testid="add-course-btn"><Plus size={16} className="mr-2" /> Add course</Button></DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New course</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="new-course-title" />
            <Textarea placeholder="Description" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="new-course-desc" />
            <Input placeholder="YouTube video ID" value={f.youtube_id} onChange={e => setF({ ...f, youtube_id: e.target.value })} data-testid="new-course-yt" />
            <Input placeholder="Duration (min)" type="number" value={f.duration_min} onChange={e => setF({ ...f, duration_min: e.target.value })} />
            <Textarea placeholder='Quiz JSON — e.g. [{"q":"..","options":["a","b","c"],"answer_index":0}]' rows={5} value={f.quiz_json} onChange={e => setF({ ...f, quiz_json: e.target.value })} data-testid="new-course-quiz" />
            <Button onClick={create} className="btn-orange hover:bg-[#FF6A00] w-full" data-testid="save-course">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map(c => (
          <div key={c.id} className="card-soft overflow-hidden" data-testid={`course-admin-${c.id}`}>
            <img src={`https://i.ytimg.com/vi/${c.youtube_id}/hqdefault.jpg`} alt="" className="w-full h-40 object-cover" />
            <div className="p-5">
              <div className="font-heading font-bold">{c.title}</div>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</p>
              <div className="mt-3 flex items-center justify-between">
                <Badge variant="secondary">{c.duration_min} min · {c.quiz?.length || 0} Q</Badge>
                <button onClick={() => del(c.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CertsAdmin() {
  const [certs, setCerts] = useState([]);
  useEffect(() => { api.get("/certificates").then(r => setCerts(r.data)); }, []);
  return (
    <div className="card-soft overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50"><tr>{["User", "Course", "Score", "Issued"].map(h => <th key={h} className="text-left p-4 text-xs uppercase tracking-wider text-slate-500 font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {certs.map(c => (
            <tr key={c.id} className="border-b border-slate-100" data-testid={`admin-cert-${c.id}`}>
              <td className="p-4 font-semibold">{c.user_name}</td>
              <td className="p-4">{c.course_title}</td>
              <td className="p-4"><Badge className="bg-[#FF6A00]/10 text-[#FF6A00] hover:bg-[#FF6A00]/10">{c.score}%</Badge></td>
              <td className="p-4 text-slate-500">{new Date(c.issued_at).toLocaleDateString()}</td>
            </tr>
          ))}
          {certs.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-500">No certificates issued yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsAdmin() {
  const [payments, setPayments] = useState([]);
  useEffect(() => { api.get("/payments").then(r => setPayments(r.data)); }, []);
  const total = payments.reduce((s, p) => s + p.amount, 0);
  return (
    <div className="space-y-4">
      <div className="card-soft p-6"><div className="text-xs uppercase tracking-wider text-slate-500">Total volume</div><div className="mt-2 font-heading font-bold text-3xl text-[#FF6A00]">₹{total.toLocaleString()}</div></div>
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Date", "Amount", "Note", "Status"].map(h => <th key={h} className="text-left p-4 text-xs uppercase tracking-wider text-slate-500 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="p-4 text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="p-4 font-semibold text-[#FF6A00]">₹{p.amount}</td>
                <td className="p-4 text-slate-500">{p.note || "—"}</td>
                <td className="p-4"><Badge className="bg-green-100 text-green-700 hover:bg-green-100">{p.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Analytics() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)); }, []);
  const users = [
    { name: "Shopkeepers", value: s.shopkeepers || 0, color: "#155EEF" },
    { name: "Freelancers", value: s.freelancers || 0, color: "#FF6A00" },
  ];
  const tasks = [
    { name: "Open", v: s.tasks_open || 0 }, { name: "Done", v: s.tasks_done || 0 },
  ];
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-soft p-6">
        <div className="font-heading font-bold mb-4">User distribution</div>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={users} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={4}>
              {users.map((u, i) => <Cell key={i} fill={u.color} />)}
            </Pie>
            <Legend />
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="card-soft p-6">
        <div className="font-heading font-bold mb-4">Task status</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={tasks}>
            <XAxis dataKey="name" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip />
            <Bar dataKey="v" fill="#155EEF" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
